import type {
  Cadence,
  ReportMeasurementPeriodOptionDto,
  ReportReportingMonthOptionDto,
  ReportTemplateDto,
} from '@act/shared/data-transfer-objects';
import { CADENCES, formatCadenceLabel } from '@act/shared/data-transfer-objects';
import { MenuItem, Select, Typography } from '@mui/material';
import { FlexContainer } from '@act/storybook';
import ReportFormField from './ReportFormField';
import ReportMeasurementPeriodSelect from './ReportMeasurementPeriodSelect';
import ReportsSurface from './ReportsSurface';
import type { ReportMetadataFormMode } from './ReportMetadataFormFields';
import type { MeasurementPeriodMatchStatus } from '../utils/reportMetadataFormOptions.util';

interface ReportMetadataReportingSectionProps {
  mode: ReportMetadataFormMode;
  customerId: string;
  cadence: Cadence;
  cadenceLabel: string;
  measurementPeriod: string;
  endMonthKey: string;
  templateId: string;
  templates: ReportTemplateDto[];
  measurementPeriodOptions: ReportMeasurementPeriodOptionDto[];
  reportingMonthOptions: ReportReportingMonthOptionDto[];
  measurementPeriodsLoading: boolean;
  reportingMonthsLoading: boolean;
  measurementPeriodMatchStatus: MeasurementPeriodMatchStatus;
  autoMatchedMeasurementPeriodLabel: string;
  onCadenceChange: (cadence: Cadence) => void;
  onMeasurementPeriodChange: (value: string) => void;
  onEndMonthKeyChange: (value: string) => void;
  onTemplateChange: (templateId: string) => void;
}

/**
 * Cadence and period fields for report metadata forms.
 * @param props - Cadence, period picker state, and handlers.
 * @returns Reporting period surface section.
 */
const ReportMetadataReportingSection = ({
  mode,
  customerId,
  cadence,
  cadenceLabel,
  measurementPeriod,
  endMonthKey,
  templateId,
  templates,
  measurementPeriodOptions,
  reportingMonthOptions,
  measurementPeriodsLoading,
  reportingMonthsLoading,
  measurementPeriodMatchStatus,
  autoMatchedMeasurementPeriodLabel,
  onCadenceChange,
  onMeasurementPeriodChange,
  onEndMonthKeyChange,
  onTemplateChange,
}: ReportMetadataReportingSectionProps) => {
  const filteredTemplates = templates.filter(
    template => !template.customerId || template.customerId === customerId,
  );

  return (
    <ReportsSurface>
      <FlexContainer
        direction="column"
        gap={2}
        width="100%"
        height="auto"
        overflowAllVisible={true}
      >
        <Typography variant="subtitle1" component="h2">
          Reporting period
        </Typography>

        {mode === 'edit' ? (
          <ReportFormField label="Cadence">
            <Typography variant="body1">{cadenceLabel}</Typography>
          </ReportFormField>
        ) : (
          <ReportFormField label="Cadence">
            <Select
              required
              fullWidth
              value={cadence}
              onChange={event => onCadenceChange(event.target.value as Cadence)}
            >
              {CADENCES.map(option => (
                <MenuItem key={option} value={option}>
                  {formatCadenceLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </ReportFormField>
        )}

        {customerId && cadence === 'adhoc' ? (
          <ReportMeasurementPeriodSelect
            label="Measurement period"
            value={measurementPeriod}
            options={measurementPeriodOptions}
            loading={measurementPeriodsLoading === true}
            onChange={onMeasurementPeriodChange}
          />
        ) : null}

        {customerId && cadence !== 'adhoc' ? (
          <>
            <ReportFormField
              label={
                cadence === 'quarterly' ? 'Quarter end month' : 'Reporting month'
              }
            >
              <Select
                required
                fullWidth
                displayEmpty
                disabled={reportingMonthsLoading === true}
                value={endMonthKey}
                onChange={event => onEndMonthKeyChange(event.target.value)}
              >
                <MenuItem value="" disabled>
                  {reportingMonthsLoading === true
                    ? 'Loading reporting months…'
                    : 'Select reporting month'}
                </MenuItem>
                {reportingMonthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" component="p">
                Anchors hero, engagement, demographics, and other monthly
                warehouse sections to this month.
              </Typography>
              {reportingMonthsLoading !== true &&
              reportingMonthOptions.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="p"
                >
                  No reporting months found in the warehouse for this customer.
                </Typography>
              ) : null}
            </ReportFormField>

            <ReportMeasurementPeriodSelect
              label="JOC measurement period (VBP & quality measures)"
              value={measurementPeriod}
              options={measurementPeriodOptions}
              loading={measurementPeriodsLoading === true}
              required={false}
              matchStatus={measurementPeriodMatchStatus}
              autoMatchedLabel={autoMatchedMeasurementPeriodLabel}
              onChange={onMeasurementPeriodChange}
            />
          </>
        ) : null}

        {mode === 'create' ? (
          <ReportFormField label="Template (optional)">
            <Select
              fullWidth
              value={templateId}
              onChange={event => onTemplateChange(event.target.value)}
              displayEmpty
            >
              <MenuItem value="">No template</MenuItem>
              {filteredTemplates.map(template => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </ReportFormField>
        ) : null}
      </FlexContainer>
    </ReportsSurface>
  );
};

export default ReportMetadataReportingSection;
